import React from "react";
import { Box, keyframes } from "@mui/material";
import { CustomChip } from "../CustomChip";

// Bouncing dots animation
const bounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
`;

// Animated dots component for processing status
const ProcessingDots: React.FC = () => {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "flex-end",
        gap: "2px",
        position: "absolute",
        bottom: "3px",
        right: "-6px",
      }}
    >
      {[0, 1, 2].map((index) => (
        <Box
          key={index}
          component="span"
          sx={{
            width: "3px",
            height: "3px",
            borderRadius: "50%",
            backgroundColor: "currentColor",
            animation: `${bounce} 1.2s ease-in-out infinite`,
            animationDelay: `${index * 0.15}s`,
          }}
        />
      ))}
    </Box>
  );
};

interface IMarketCampaignStatusProps {
  status: string;
}

export const MarketCampaignStatusRenderer: React.FC<
  IMarketCampaignStatusProps
> = (props) => {
  const capitalizeFirstLetter = (text: string): string => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };
  const status = (props.status || "").toLowerCase();

  // Handle AUTO_GENERATING status with animated dots
  if (status === "auto_generating") {
    return (
      <CustomChip
        color="info"
        content={
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "flex-end",
              position: "relative",
              pr: 1.5,
            }}
          >
            <span>AUTO_GENERATING</span>
            <ProcessingDots />
          </Box>
        }
        size="small"
        sx={{
          backgroundColor: "#FFF3E0",
          borderColor: "#FB8C00",
          "& .MuiChip-label": {
            color: "#E65100",
          },
          color: "#E65100",
        }}
      />
    );
  }

  if (
    status === "auto_generated_failed" ||
    status === "AUTO_GENERATED_FAILED"
  ) {
    return (
      <CustomChip
        color="error"
        content={status ? capitalizeFirstLetter(status) : "--"}
        size="small"
      />
    );
  }

  if (status === "ready_to_execute") {
    return (
      <CustomChip
        color="primary"
        content={status ? capitalizeFirstLetter(status) : "--"}
        size="small"
      />
    );
  }

  if (status === "SCHEDULED" || status === "scheduled") {
    return (
      <CustomChip
        color="primary"
        content={status ? capitalizeFirstLetter(status) : "--"}
        size="small"
      />
    );
  } else if (status === "running" || status === "RUNNING") {
    return (
      <CustomChip
        color="warning"
        content={status ? capitalizeFirstLetter(status) : "--"}
        size="small"
      />
    );
  } else if (status === "cancelled" || status === "CANCELLED") {
    return (
      <CustomChip
        color="error"
        content={status ? capitalizeFirstLetter(status) : "--"}
        size="small"
        sx={{ color: "#fff" }}
      />
    );
  } else if (status === "done" || status === "DONE") {
    return (
      <CustomChip
        color="success"
        content={status ? capitalizeFirstLetter(status) : "--"}
        size="small"
      />
    );
  } else if (status === "draft" || status === "DRAFT") {
    return (
      <CustomChip
        color="secondary"
        content={status ? capitalizeFirstLetter(status) : "--"}
        size="small"
        sx={{ color: "#fff" }}
      />
    );
  } else {
    return <CustomChip color="info" content={status || "--"} size="small" />;
  }
};

interface IMarketCampaignMessageCountProps {
  messageCount: number | null | undefined;
  type: "TOTAL_MESSAGES" | "SENT_MESSAGES" | "FAILED_MESSAGES";
}

export const MarketCampaignMessageCountRenderer: React.FC<
  IMarketCampaignMessageCountProps
> = (props) => {
  const capitalizeFirstLetter = (text: number): string => {
    if (!text) return "";
    return text.toString().charAt(0).toUpperCase() + text.toString().slice(1);
  };
  const messageCount = props.messageCount;
  const type = props.type;

  // Handle AUTO_GENERATING status with animated dots
  if (type === "TOTAL_MESSAGES") {
    return (
      <CustomChip
        color="primary"
        content={messageCount ? capitalizeFirstLetter(messageCount) : "0"}
        size="small"
      />
    );
  } else if (type === "FAILED_MESSAGES") {
    return (
      <CustomChip
        color="error"
        content={messageCount ? capitalizeFirstLetter(messageCount) : "0"}
        size="small"
      />
    );
  } else if (type === "SENT_MESSAGES") {
    return (
      <CustomChip
        color="success"
        content={messageCount ? capitalizeFirstLetter(messageCount) : "0"}
        size="small"
      />
    );
  } else {
    return (
      <CustomChip color="info" content={messageCount || "0"} size="small" />
    );
  }
};
